'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import { GroupForm, type GroupFormRef } from '@/components/partials/groups'
import { CreateGroupParams } from '@/types/group'
import { useCreateGroup } from '@/hooks'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const groupFormRef = useRef<GroupFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createGroup, isPending: isLoading } = useCreateGroup()

  const handleSubmit = (data: CreateGroupParams) => {
    createGroup(data, {
      onSuccess: async newGroup => {
        setIsRedirecting(true)
        queryClient.setQueryData(['group', newGroup.id], newGroup)
        await queryClient.invalidateQueries({ queryKey: ['groups'] })
        toast.success(t('created_successfully'))
        router.push(`/users/groups/${newGroup.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateGroupParams) => {
    createGroup(data, {
      onSuccess: async newGroup => {
        queryClient.setQueryData(['group', newGroup.id], newGroup)
        await queryClient.invalidateQueries({ queryKey: ['groups'] })
        const detailUrl = `/users/groups/${newGroup.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        groupFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (groupFormRef.current) {
        groupFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/users/groups')
  }

  return (
    <GroupForm
      key={formKey}
      ref={groupFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
